// SaaS mode: on a company subdomain, loads the company (name for screens and documents) before
// rendering; an unknown address shows "Company not found". On the root domain only the Qayd site,
// sign-up and legal pages are available — sign-in pages ask for the company's address instead.
import { useEffect, useState } from "react";
import { useLocation } from "lib/router";
import { SAAS_MODE, TENANT_SLUG, rootUrl } from "config/tenant";
import { Navigate } from "lib/router";
import { COMPANY } from "config/company";
import SaasServices from "services/Saas";
import { CircleLoading } from "components/Loaders";
import FindCompany from "views/Saas/FindCompany";
import CompanyNotFound from "views/Saas/CompanyNotFound";
import SubscriptionEnded from "views/Saas/SubscriptionEnded";
import Microsite from "views/Saas/Microsite";

const ROOT_DOMAIN_PATHS = ["/", "/signup", "/privacy-policy", "/privacy-policy-v2", "/kit", "/kit/pdf"];

function CompanyHome() {
  const [page, setPage] = useState(undefined);
  useEffect(() => {
    SaasServices.companyPage(TENANT_SLUG)
      .then((res) => setPage(res?.data?.site || null))
      .catch(() => setPage(null));
  }, []);
  if (page === undefined) return <CircleLoading />;
  if (!page) return <Navigate to="/login" replace />;
  return <Microsite name={COMPANY.name} site={page} />;
}

export default function TenantGate({ children }) {
  const { pathname } = useLocation();
  const [state, setState] = useState(TENANT_SLUG ? "loading" : "ready");

  useEffect(() => {
    if (!SAAS_MODE || !TENANT_SLUG) return;
    let cancelled = false;
    SaasServices.companyInfo(TENANT_SLUG)
      .then((res) => {
        if (cancelled) return;
        const company = res?.data;
        if (!company) return setState("missing");
        COMPANY.name = company.name;
        COMPANY.type = company.type;
        COMPANY.slug = company.slug;
        // contact details from the company's website settings → printed on documents
        if (company.profile) {
          Object.assign(COMPANY, {
            logoUrl: company.profile.logo_url || COMPANY.logoUrl,
            address: company.profile.address || COMPANY.address,
            phone: company.profile.phone || COMPANY.phone,
            email: company.profile.email || COMPANY.email,
            website: company.profile.website || COMPANY.website,
            complaintsWhatsapp: company.profile.whatsapp || COMPANY.complaintsWhatsapp,
          });
        }
        document.title = `${company.name} · Qayd`;
        setState(company.subscription && company.subscription.access === false ? "ended" : "ready");
      })
      .catch(() => !cancelled && setState("missing"));
    return () => {
      cancelled = true;
    };
  }, []);

  if (!SAAS_MODE) return children;
  if (TENANT_SLUG) {
    if (state === "loading") return <CircleLoading />;
    if (state === "missing") return <CompanyNotFound slug={TENANT_SLUG} />;
    if (state === "ended") return <SubscriptionEnded name={COMPANY.name} />;
    // The company's own address shows its public page (or sign-in when it has none).
    if (pathname === "/") return <CompanyHome />;
    if (pathname === "/signup") {
      window.location.replace(rootUrl("/signup"));
      return null;
    }
    return children;
  }
  if (!ROOT_DOMAIN_PATHS.includes(pathname)) return <FindCompany />;
  return children;
}
